import React, { useEffect } from 'react';
import classNameBind from 'classnames/bind';
import {
  Text,
  Stack,
  Image,
  withTranslation,
  Spinner,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';
import { withInject, errorBoundary } from '@piwikpro/platform';
import { connect, ConnectedProps } from 'react-redux';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import CookieInformationCreateAccount from './CookieInformationCreateAccount';
import CookieInformationConnectAccount from './CookieInformationConnectAccount';
import CookieInformationAccountPreview from './CookieInformationAccountPreview';
import CookieInformationCreateAccountSuccess from './CookieInformationCreateAccountSuccess';
import logo from './cookieInformationLogo.png';
import styles from './CookieInformationAccount.module.css';
import { CookieInformationService } from '../../CookieInformationService';
import { CookieInformationState } from '../../redux';

const bindClasses = classNameBind.bind(styles);

const mapStateToProps = (state: CookieInformationState) => ({
  accountState: state.cookieInformation.account,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  cookieInformation: CookieInformationService
}

interface Props extends InjectedProps, ReduxProps {
  domains?: string[]
  onDirtyChange?: (modified: boolean) => void
  loggedUserEmail: SessionState['email'] | null
  inModal?: boolean
  t: TFunction
}

interface AccountLayoutProps {
  header: React.ReactNode
  children: React.ReactNode
  contentSpacing?: 'narrow' | 'wide'
}

const AccountLayout: React.FC<AccountLayoutProps> = ({
  header,
  children,
  contentSpacing = 'wide',
}) => (
  <Stack vertical spacing="wide">
    <Stack.Item fill>
      <Stack>
        <Stack.Item fill>
          {header}
        </Stack.Item>
        <Stack.Item>
          <div className={bindClasses('logo')}>
            <Image src={logo} alt="Cookie Information" />
          </div>
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item fill previousSpacing={contentSpacing}>
      {children}
    </Stack.Item>
  </Stack>
);

export const CookieInformationAccount: React.FC<Props> = ({
  cookieInformation,
  accountState,
  loggedUserEmail,
  onDirtyChange,
  inModal = false,
  t,
  domains,
}) => {
  useEffect(() => {
    if (accountState.status === 'awaitingAccountLoad') {
      cookieInformation.getAccount();
    }
  }, []);

  switch (accountState.status) {
    case 'awaitingAccountLoad':
    case 'loadingAccount':
      return <Spinner />;
    case 'showingLinkedAccount':
      return (
        <AccountLayout
          header={(
            <div className={bindClasses('sectionLabel')}>
              <Text as="p" size={12} weight={600} color="InkLighter" dataId="Label">
                {t('cookieInformation.account.accountLabel')}
              </Text>
            </div>
          )}
          contentSpacing="narrow"
        >
          <CookieInformationAccountPreview accountName={accountState.accountName} inModal={inModal} />
        </AccountLayout>
      );
    case 'showingCreateAccountSuccess':
      return (
        <Stack vertical spacing="wide">
          <Stack.Item fill>
            <CookieInformationCreateAccountSuccess />
          </Stack.Item>
        </Stack>
      );
    case 'showingConnectAccountForm':
      return (
        <AccountLayout
          header={(
            <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
              {t('cookieInformation.account.addAccount')}
            </Text>
          )}
        >
          <CookieInformationConnectAccount onDirtyChange={onDirtyChange} />
        </AccountLayout>
      );
    case 'showingCreateAccountForm':
      return (
        <AccountLayout
          header={(
            <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
              {t('cookieInformation.account.createAccount')}
            </Text>
          )}
        >
          <CookieInformationCreateAccount
            onDirtyChange={onDirtyChange}
            loggedUserEmail={loggedUserEmail}
            domains={domains}
          />
        </AccountLayout>
      );
    default:
      return null;
  }
};

export default connector(withInject<InjectedProps>({
  cookieInformation: 'CookieInformationCrate.CookieInformationService',
})(withTranslation(['administration'])(errorBoundary(CookieInformationAccount))));
